import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { getRewards, redeemReward } from './api';

const RewardsList = ({ userEmail, userPhone, isRegistered, remainingBeans, onRedeemSuccess }) => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const rewardsData = await getRewards();
      setRewards(rewardsData);
    } catch (err) {
      console.error('Error fetching rewards:', err);
      setError('Failed to load rewards. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemReward = async (reward) => {
    if (!isRegistered) {
      setError('You must register first to redeem rewards');
      return;
    }

    if (remainingBeans < reward.beans) {
      setError(`Not enough beans. You need ${reward.beans} beans but have ${remainingBeans}.`);
      return;
    }

    try {
      setRedeeming(true);
      setError(null);

      await redeemReward(reward, userEmail, userPhone);
      
      setSuccessMessage(`Successfully redeemed ${reward.Title}!`);
      setSuccess(true);
      onRedeemSuccess();
    } catch (err) {
      console.error('Reward redemption error:', err);
      setError(err.response?.data?.detail || 'Failed to redeem reward. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Available Rewards
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Your remaining beans: <strong>{remainingBeans}</strong>
      </Typography>
      
      {rewards.length === 0 ? (
        <Typography variant="body1">No rewards available at this time.</Typography>
      ) : (
        <Grid container spacing={3}>
          {rewards.map((reward, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {reward.imageurl && (
                  <CardMedia
                    component="img"
                    height="140"
                    image={reward.imageurl}
                    alt={reward.Title}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="div">
                    {reward.Title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reward.description || 'No description available'}
                  </Typography>
                  <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                    {reward.beans} beans
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!isRegistered || redeeming || remainingBeans < reward.beans}
                    onClick={() => handleRedeemReward(reward)}
                  >
                    {redeeming ? 'Redeeming...' : 'Redeem'}
                  </Button>
                </CardActions>
                {!isRegistered && (
                  <Typography variant="caption" color="error" sx={{ px: 2, pb: 1, textAlign: 'center' }}>
                    Registration required
                  </Typography>
                )}
                {isRegistered && remainingBeans < reward.beans && (
                  <Typography variant="caption" color="error" sx={{ px: 2, pb: 1, textAlign: 'center' }}>
                    Not enough beans
                  </Typography>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      <Snackbar 
        open={error !== null} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(false)}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default RewardsList;
